import React, {useEffect, useState} from "react";
import '../App.css';
import {searchSubscriberKyc} from "../api/dataRetrieve";
import {extractNames, formatDate, formatInputDate, getLoggedInUser} from "../utils/functions";
import {Link, NavigateFunction, useNavigate} from "react-router-dom";
import {UpdateSubscriber} from "./UpdateSubsriber";
import {UpdateSubscriberBOCRA} from "../components/UpdateSubscriberBOCRA";
import {UserInfo} from "../types/interfaces";

export const MainPage = () => {
    const navigate: NavigateFunction = useNavigate();
    // State management for the application
    const [inputValue, setInputValue] = useState(''); // Search input field value
    const [searchType, setSearchType] = useState('msisdn'); // Type of search (MSISDN or ID)
    const [loading, setLoading] = useState(false); // Loading state for API calls
    const [error, setError] = useState(''); // Error message state
    const [searchResult, setSearchResult] = useState<any>(null); // Search results from API
    const [showEditForm, setShowEditForm] = useState(false); // Toggle for edit form display
    const [showRegisterForm, setShowRegisterForm] = useState(false); // Toggle for registration form
    const [currentSubscriber, setCurrentSubscriber] = useState(null)
    const [currentUser, setCurrentUser] = useState<UserInfo|null>(null);

    useEffect(() :void => {
        const getCurrentUser = async () => {
            const loggedInUser: UserInfo | null = await getLoggedInUser()
            if (loggedInUser === null) {
                navigate('/error')
            }
            setCurrentUser(loggedInUser)
        }
        getCurrentUser()

    }, []);

    // Form data for both edit and registration forms
    const [formData, setFormData] = useState<any>({
        firstName: '',
        lastName: '',
        msisdn: '',
        alternativePhone: '',
        plotNumber: '',
        street: '',
        cityTownVillage: '',
        city: '',
        billAddress4: '',
        zipCode: '',
        nextOfKin: '',
        nextOfKinPhone: '',
        emailAddress: '',
        gender: 'MALE',
        dateOfBirth: '',
        nationality: 'Botswana',
        idType: 'NATIONAL_ID',
        idNumber: '',
        idIssueDate: '',
        idExpiryDate: '',
        newPhoneNumber: '', // Field for adding new phone number to existing subscriber
        // Add fields for other permit types
        residencePermitNo: '',
        residencePermitDateOfIssue: '',
        residencePermitDateOfExpiry: '',
        workPermitNo: '',
        workPermitDateOfIssue: '',
        workPermitDateOfExpiry: '',
        visaNo: '',
        visaDateOfIssue: '',
        visaDateOfExpiry: '',
        refugeeIdNo: ''
    });

    const handleSearch = async () => {
        if (!inputValue.trim()) {
            setError('Please enter a value to search');
            return;
        }

        setLoading(true);
        setError('');
        setSearchResult(null);
        setShowRegisterForm(false);

        try {
            setCurrentSubscriber(null);

            const result: any = searchType === 'msisdn'
                ? await searchSubscriberKyc(inputValue, undefined)
                : await searchSubscriberKyc(undefined, inputValue);

            setCurrentSubscriber(result);
            setSearchResult(result);
        } catch (error: any) {
            console.error('Search error:', error);
            setError(error.response?.data?.message || 'An error occurred during search');
        } finally {
            setLoading(false);
        }
    };

    const renderStatusIndicator = (state: string | null | undefined, verificationReason: string | null | undefined = null, considerVerificationReason: boolean = false) => {
        if (!state) return null;

        // Only check verification reason for the main status indicator
        if (considerVerificationReason && verificationReason && verificationReason.toLowerCase().includes('unmatching fields')) {
            return (
                <div className="status-indicator">
                    <span className="status-dot status-failed"></span>
                    <span className="status-text">Failed Verification</span>
                </div>
            );
        }

        let statusClass = '';
        switch(state.toUpperCase()) {
            case 'ACTIVE':
                statusClass = 'status-active';
                break;
            case 'PENDING':
                statusClass = 'status-pending';
                break;
            default:
                statusClass = 'status-failed';
        }

        return (
            <div className="status-indicator">
                <span className={`status-dot ${statusClass}`}></span>
                <span className="status-text">{state}</span>
            </div>
        );
    };

    const getDisplayValueFromObject = (obj: any, path: string, defaultValue: any = 'N/A') => {
        if (!obj) return defaultValue;

        const keys = path.split('.');
        let result = obj;

        for (const key of keys) {
            if (result === null || result === undefined || typeof result !== 'object') {
                return defaultValue;
            }
            result = result[key];
        }

        return result || defaultValue;
    };

    /**
     * Handle edit button click to prepare edit form
     *
     * This function:
     * 1. Extracts data from search result
     * 2. Pre-populates the edit form
     * 3. Handles data structure variations
     */

    const renderSearchResults = () => {
        if (!searchResult) return null;

        // Add a console log to see the actual structure

        // Extract fields from either response structure
        // Personal details
        const firstName = getDisplayValueFromObject(searchResult, 'firstName');
        const lastName = getDisplayValueFromObject(searchResult, 'lastName');
        const naturalSubscriberName = getDisplayValueFromObject(searchResult, 'naturalSubscriber');

        // Build the fullName in priority order:
        // 1. Use naturalSubscriber if available
        // 2. Combine firstName and lastName if both are available
        // 3. Use whatever is available
        const fullName = naturalSubscriberName !== 'N/A' ? naturalSubscriberName :
            (firstName !== 'N/A' && lastName !== 'N/A' ? `${firstName} ${lastName}` :
                (firstName !== 'N/A' ? firstName :
                    (lastName !== 'N/A' ? lastName : 'N/A')));

        // Gender/Sex
        const gender = getDisplayValueFromObject(searchResult, 'sex') || getDisplayValueFromObject(searchResult, 'gender');

        // Date of birth
        const dateOfBirth = getDisplayValueFromObject(searchResult, 'dateOfBirth');

        // Nationality/Country
        const nationality = getDisplayValueFromObject(searchResult, 'nationality', null) || getDisplayValueFromObject(searchResult, 'country');

        // Document info
        const documentNumber = getDisplayValueFromObject(searchResult, 'documentNumber', null) ||
            getDisplayValueFromObject(searchResult, 'subscriberDocuments.0.documentNumber');
        const documentType = getDisplayValueFromObject(searchResult, 'documentType', null) ||
            getDisplayValueFromObject(searchResult, 'subscriberDocuments.0.documentType');
        const idIssueDate = getDisplayValueFromObject(searchResult, 'subscriberDocuments.0.dateOfIssue');
        const idExpiryDate = getDisplayValueFromObject(searchResult, 'subscriberDocuments.0.expiryDate');

        // MSISDN info
        const msisdn = getDisplayValueFromObject(searchResult, 'msisdn');
        const state = getDisplayValueFromObject(searchResult, 'state') || getDisplayValueFromObject(searchResult, 'verificationState');
        const verificationReason = getDisplayValueFromObject(searchResult, 'verificationReason');
        const numberType = getDisplayValueFromObject(searchResult, 'numberType');
        const activationDate = getDisplayValueFromObject(searchResult, 'activationDate');
        const mnoId = getDisplayValueFromObject(searchResult, 'mnoId');
        const subscriberId = getDisplayValueFromObject(searchResult, 'id');
        const created = getDisplayValueFromObject(searchResult, 'created');
        const updated = getDisplayValueFromObject(searchResult, 'updated');

        // Address info
        const plotNumber = getDisplayValueFromObject(searchResult, 'addresses.0.plotWardBox');
        const city = getDisplayValueFromObject(searchResult, 'addresses.0.cityTown');
        const addressType = getDisplayValueFromObject(searchResult, 'addresses.0.addressType');

        // Collections
        const phoneNumbersFromNumbers = Array.isArray(getDisplayValueFromObject(searchResult, 'numbers', []))
            ? getDisplayValueFromObject(searchResult, 'numbers', []).map((num: any) => ({
                msisdn: num.msisdn,
                state: num.state
            }))
            : [];

        // Legacy collections
        const phoneNumbers = getDisplayValueFromObject(searchResult, 'phoneNumbers', []);
        const phones = getDisplayValueFromObject(searchResult, 'phones', []);
        const naturalSubscriberPhones = getDisplayValueFromObject(searchResult, 'naturalSubscriberPhones', []);

        // Combine all possible phone number arrays
        const allPhoneNumbers = [
            ...(Array.isArray(phoneNumbers) ? phoneNumbers : []),
            ...(Array.isArray(phones) ? phones : []),
            ...(Array.isArray(naturalSubscriberPhones) ? naturalSubscriberPhones : []),
            ...(Array.isArray(phoneNumbersFromNumbers) ? phoneNumbersFromNumbers : []),
            // If this subscriber has this msisdn, include it as well
            ...(msisdn && msisdn !== 'N/A' ? [{ msisdn, state }] : [])
        ];

        // Filter out duplicates based on msisdn
        const uniquePhones = Array.from(new Set(
            allPhoneNumbers
                .filter(phone => phone && (phone.msisdn || phone.number)) // Filter out null/undefined phones
                .map(phone => phone.msisdn || phone.number) // Handle both formats
        )).map(number => {
            // Find the original phone object to get its state
            const originalPhone = allPhoneNumbers.find(p =>
                (p.msisdn === number || p.number === number)
            );
            return {
                msisdn: number,
                state: originalPhone?.state
            };
        });

        // In the renderSearchResults function, add these new extractions
        const juristicSubscriber = getDisplayValueFromObject(searchResult, 'juristicSubscriber');
        const registrationNumber = getDisplayValueFromObject(searchResult, 'registrationNumber');

        // Create a formatted display value based on available data
        const displayValue = (value: any): string => value || 'N/A';

        // Render the search results UI
        return (
            <div className="form-container result-container">
                <h3>Subscriber Information {renderStatusIndicator(state, verificationReason, true)}</h3>
                <div className="result-section">
                    <h4>Personal Details</h4>
                    <div className="result-grid">
                        <div className="result-item">
                            <span className="result-label">First Name:</span>
                            <span className="result-value">{displayValue(firstName)}</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Last Name:</span>
                            <span className="result-value">{displayValue(lastName)}</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Full Name:</span>
                            <span className="result-value">{displayValue(fullName)}</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Gender:</span>
                            <span className="result-value">{displayValue(gender)}</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Date of Birth:</span>
                            <span className="result-value">{formatDate(dateOfBirth)}</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Nationality:</span>
                            <span className="result-value">{displayValue(nationality)}</span>
                        </div>
                        {created !== 'N/A' && (
                            <div className="result-item">
                                <span className="result-label">Registered:</span>
                                <span className="result-value">{formatDate(created)}</span>
                            </div>
                        )}
                        {updated !== 'N/A' && (
                            <div className="result-item">
                                <span className="result-label">Last Updated:</span>
                                <span className="result-value">{formatDate(updated)}</span>
                            </div>
                        )}
                    </div>
                </div>

                {(juristicSubscriber !== 'N/A' || registrationNumber !== 'N/A') && (
                    <div className="result-section">
                        <h4>Organization Details</h4>
                        <div className="result-grid">
                            {juristicSubscriber !== 'N/A' && (
                                <div className="result-item">
                                    <span className="result-label">Organization Name:</span>
                                    <span className="result-value">{displayValue(juristicSubscriber)}</span>
                                </div>
                            )}
                            {registrationNumber !== 'N/A' && (
                                <div className="result-item">
                                    <span className="result-label">Registration Number:</span>
                                    <span className="result-value">{displayValue(registrationNumber)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="result-section">
                    <h4>Contact Information</h4>
                    <div className="result-grid">
                        {uniquePhones.length > 0 ? (
                            uniquePhones.map((phone: any, index: number) => (
                                <div key={index} className="result-item">
                                    <span className="result-label">MSISDN {uniquePhones.length > 1 ? index + 1 : ''}:</span>
                                    <span className="result-value">{phone.msisdn} {phone.state && renderStatusIndicator(phone.state)}</span>
                                </div>
                            ))
                        ) : (
                            <div className="result-item">
                                <span className="result-label">MSISDN:</span>
                                <span className="result-value">{displayValue(msisdn)}</span>
                            </div>
                        )}
                        {numberType !== 'N/A' && (
                            <div className="result-item">
                                <span className="result-label">Number Type:</span>
                                <span className="result-value">{displayValue(numberType)}</span>
                            </div>
                        )}
                        {activationDate !== 'N/A' && (
                            <div className="result-item">
                                <span className="result-label">Activation Date:</span>
                                <span className="result-value">{formatDate(activationDate)}</span>
                            </div>
                        )}
                        {verificationReason !== 'N/A' && (
                            <div className="result-item">
                                <span className="result-label">Verification Message:</span>
                                <span className="result-value">{displayValue(verificationReason)}</span>
                            </div>
                        )}
                    </div>
                </div>

                {(plotNumber !== 'N/A' || city !== 'N/A') && (
                    <div className="result-section">
                        <h4>Address Information</h4>
                        <div className="result-grid">
                            {plotNumber !== 'N/A' && (
                                <div className="result-item">
                                    <span className="result-label">Plot Number:</span>
                                    <span className="result-value">{displayValue(plotNumber)}</span>
                                </div>
                            )}
                            {city !== 'N/A' && (
                                <div className="result-item">
                                    <span className="result-label">City/Town:</span>
                                    <span className="result-value">{displayValue(city)}</span>
                                </div>
                            )}
                            {addressType !== 'N/A' && (
                                <div className="result-item">
                                    <span className="result-label">Address Type:</span>
                                    <span className="result-value">{displayValue(addressType)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="result-section">
                    <h4>Identification Details</h4>
                    <div className="result-grid">
                        <div className="result-item">
                            <span className="result-label">ID Type:</span>
                            <span className="result-value">{displayValue(documentType)}</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">ID Number:</span>
                            <span className="result-value">{displayValue(documentNumber)}</span>
                        </div>
                        {idIssueDate !== 'N/A' && (
                            <div className="result-item">
                                <span className="result-label">Issue Date:</span>
                                <span className="result-value">{formatDate(idIssueDate)}</span>
                            </div>
                        )}
                        {idExpiryDate !== 'N/A' && (
                            <div className="result-item">
                                <span className="result-label">Expiry Date:</span>
                                <span className="result-value">{formatDate(idExpiryDate)}</span>
                            </div>
                        )}
                    </div>
                </div>

                {(subscriberId !== 'N/A' || mnoId !== 'N/A') && (
                    <div className="result-section">
                        <h4>System Information</h4>
                        <div className="result-grid">
                            {subscriberId !== 'N/A' && (
                                <div className="result-item">
                                    <span className="result-label">Subscriber ID:</span>
                                    <span className="result-value">{displayValue(subscriberId)}</span>
                                </div>
                            )}
                            {mnoId !== 'N/A' && (
                                <div className="result-item">
                                    <span className="result-label">MNO ID:</span>
                                    <span className="result-value">{displayValue(mnoId)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="button-group">
                    <button className="btn primary" onClick={() => setSearchResult(null)}>New Search</button>
                </div>

                {/* Debug section */}
                <div className="debug-section">
                    <details>
                        <summary>View Raw Data</summary>
                        <pre className="result-data">
              {JSON.stringify(searchResult, null, 2)}
            </pre>
                    </details>
                </div>
            </div>
        );
    };

    // Render the edit form
    const renderEditForm = () => {
        return (
            <div className="form-container">
                <h3>Edit Subscriber Information</h3>
                {error && <div className="error-message">{error}</div>}
                <UpdateSubscriberBOCRA subscriber={currentSubscriber} />
            </div>
        );
    };

    return (
        <div className="App">
            <main className="App-main">
                <div className="content-container">
                    <section className="form-section">
                        <div className="form-container search-container">
                            <h2>KYC Form</h2>

                            <div className="input-group">
                                <label htmlFor="search-input">MSISDN:</label>
                                <input
                                    type="text"
                                    id="search-input"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder={`Enter ${searchType === 'msisdn' ? 'MSISDN' : 'ID Number'}`}
                                />
                            </div>

                            <div className="radio-group">
                                <label>
                                    <input
                                        type="radio"
                                        name="searchType"
                                        value="msisdn"
                                        checked={searchType === 'msisdn'}
                                        onChange={() => setSearchType('msisdn')}
                                    />
                                    MSISDN
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="searchType"
                                        value="id"
                                        checked={searchType === 'id'}
                                        onChange={() => setSearchType('id')}
                                    />
                                    ID
                                </label>
                            </div>

                            {error && <div className="error-message">{error}</div>}

                            <div className="button-group">
                                <button
                                    className="btn primary"
                                    onClick={handleSearch}
                                    disabled={loading}
                                >
                                    {loading ? 'Searching...' : 'Search'}
                                </button>
                                <Link
                                    className="btn register"
                                    to='/register-subscriber'
                                >
                                    Register
                                </Link>
                            </div>
                        </div>

                        {!showEditForm && !showRegisterForm && searchResult && renderSearchResults()}
                    </section>

                    {!showEditForm && !showRegisterForm && (

                        <section className="info-section p-4">
                            <div className="flow-description bg-white p-6 rounded-lg shadow-md">
                                <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-green-600 pb-2">How It Works</h3>

                                <div className="flow-item pt-4 border-t-2 border-gray-200 mt-6">
                                    <h4 className="text-xl font-semibold text-gray-700 mb-2">Troubleshooting Tips</h4>
                                    <p className="text-gray-600 mb-1">If you encounter issues, consider the following:</p>
                                    <ul className="list-disc list-inside text-gray-600 pl-4 space-y-1">
                                        <li>For <span className="error-message">Insufficient Rights</span> errors, please <span className="font-semibold text-blue-700">contact administrator</span> to request the necessary permissions.</li>
                                    </ul>
                                </div>

                                <div className="flow-item mb-6 pb-4 border-b border-gray-200 last:border-b-0 last:mb-0 last:pb-0">
                                    <h4 className="text-xl font-semibold text-gray-700 mb-2">Search</h4>
                                    <p className="text-gray-600 mb-1">Enter an MSISDN or ID number to search for existing subscriber records in the database.</p>
                                    <p className="flow-details text-gray-500 text-sm italic">The system checks both BOCRA.</p>
                                </div>

                                <div className="flow-item mb-6 pb-4 border-b border-gray-200 last:border-b-0 last:mb-0 last:pb-0">
                                    <h4 className="text-xl font-semibold text-gray-700 mb-2">Register</h4>
                                    <p className='font-bold text-red-600 mb-1'>Use the Register / Registration form for all New registration, Adding a number or Updating KYC.</p>
                                    <p className="flow-details text-gray-500 text-sm italic">New subscribers must provide valid identification and contact details.</p>
                                </div>
                            </div>
                        </section>
                    )}
                </div>
            </main>

        </div>
    );
}
